import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import queryString from 'query-string';
import { apiClient } from 'lib/api';
import type { QueryConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import type { PinterestCampaignWithAdGroups } from './types';

export interface GetPinterestAdGroupsParams {
  accountId: string;
  campaignIds: string[];
}

export const getPinterestAdGroups = async (
  { params }: { params: GetPinterestAdGroupsParams },
  signal?: AbortSignal,
): Promise<PinterestCampaignWithAdGroups[]> => {
  const res = await apiClient.get<PinterestCampaignWithAdGroups[]>(queryKeys.adGroups, {
    signal,
    params: {
      account_id: params.accountId,
      campaign_ids: params.campaignIds,
    },
    paramsSerializer: (prms) => queryString.stringify(prms, { arrayFormat: 'comma' }),
  });

  if (isAxiosError(res)) {
    const error = new Error('Get Pinterest Ad Groups Failed');
    error.cause = res.response?.data;
    return Promise.reject(error);
  }

  return res.data;
};

interface UsePinterestAdGroupsOptions<TData> {
  params: GetPinterestAdGroupsParams;
  config?: QueryConfig<typeof getPinterestAdGroups, TData>;
}

export const usePinterestAdGroups = <TData = PinterestCampaignWithAdGroups[]>({
  params,
  config,
}: UsePinterestAdGroupsOptions<TData>) =>
  useQuery({
    staleTime: 0,
    refetchOnMount: true,
    retry: false,
    ...config,
    enabled: (config?.enabled ?? true) && !!params.accountId && params.campaignIds.length > 0,
    queryFn: ({ signal }) => getPinterestAdGroups({ params }, signal),
    queryKey: [queryKeys.adGroups, params.accountId, params.campaignIds],
  });
